import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import humps from 'humps';
import i18n from '../i18n';
import { useAuthModalStore } from '../stores/useAuthModalStore';
import { useAuthStore } from '../stores/useAuthStore';
import { useMaintenanceStore } from '../stores/useMaintenanceStore';

// Constants
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

/**
 * Response envelope emitted by the API middleware (and Caddy fallback) during
 * deploy/maintenance windows. SPA and VTO widget detect this shape, show a
 * banner, and pause polling/submits instead of erroring out.
 */
export interface MaintenanceEnvelope {
    code: 'MAINTENANCE';
    message?: string;
    retry_after_seconds?: number;
}

export class MaintenanceError extends Error {
    public readonly retryAfterSeconds: number;
    public readonly isMaintenance = true;

    constructor(retryAfterSeconds: number, message = 'Deploy em andamento') {
        super(message);
        this.name = 'MaintenanceError';
        this.retryAfterSeconds = retryAfterSeconds;
    }
}

// ApiClient class to manage HTTP requests
class ApiClient {
    private static instance: ApiClient;
    public client: AxiosInstance;

    private constructor() {
        this.client = axios.create({
            baseURL: API_URL,
            // Using Bearer tokens instead of cookies - no need for withCredentials
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
        });

        // Request interceptor to attach Bearer token
        this.client.interceptors.request.use(
            (config: InternalAxiosRequestConfig) => {
                // Attach Bearer token from localStorage or use existing header
                if (!config.headers['Authorization']) {
                    const token = localStorage.getItem('auth_token');
                    if (token) {
                        config.headers['Authorization'] = `Bearer ${token}`;
                    }
                }

                // Set Accept-Language header based on current locale
                const currentLocale = localStorage.getItem('i18nextLng') || 'pt-BR';
                const acceptLanguage = currentLocale.startsWith('pt') ? 'pt-br' : 'en';
                config.headers['Accept-Language'] = acceptLanguage;

                // Convert request data from camelCase to snake_case
                if (config.data && typeof config.data === 'object' &&
                    !(config.data instanceof FormData) &&
                    !(config.data instanceof Blob)) {
                    config.data = humps.decamelizeKeys(config.data);
                }

                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Response interceptor to handle authentication errors and transform data
        this.client.interceptors.response.use(
            (response: AxiosResponse) => {
                // Any 2xx clears the maintenance flag
                useMaintenanceStore.getState().notifySuccess();

                // Transform snake_case to camelCase in response data
                // Skip transformation for binary data like Blob and FormData
                if (response.data && typeof response.data === 'object' &&
                    !(response.data instanceof FormData) &&
                    !(response.data instanceof Blob)) {
                    response.data = humps.camelizeKeys(response.data);
                }
                return response;
            },
            (error) => this.handleResponseError(error)
        );
    }

    /**
     * Detects the API maintenance envelope returned by
     * PreventRequestsDuringMaintenance middleware (API) or the Caddy 502/504
     * fallback. Updates the global store so MaintenanceBanner + submit guards
     * react everywhere.
     *
     * The production Caddy converts 502/504 into 503 with the MAINTENANCE
     * envelope, but we also treat raw 502/504 as maintenance as a defensive
     * fallback for environments where that handle_errors block is missing
     * (staging, local proxies).
     */
    private detectMaintenance(error: AxiosError): MaintenanceError | null {
        const status = error.response?.status;
        if (status !== 503 && status !== 502 && status !== 504) {
            return null;
        }

        const data = error.response?.data as MaintenanceEnvelope | undefined;
        const retryHeader = error.response?.headers?.['retry-after'];
        const retryFromHeader = retryHeader ? parseInt(String(retryHeader), 10) : NaN;
        const retryFromBody = data?.retry_after_seconds;
        const retryAfterSeconds = Number.isFinite(retryFromHeader)
            ? retryFromHeader
            : (retryFromBody ?? 30);

        useMaintenanceStore.getState().notifyMaintenance(retryAfterSeconds);

        return new MaintenanceError(retryAfterSeconds, data?.message);
    }

    // Singleton pattern to ensure a single instance
    public static getInstance(): ApiClient {
        if (!ApiClient.instance) {
            ApiClient.instance = new ApiClient();
        }
        return ApiClient.instance;
    }

    // URLs onde 401 e fluxo normal (ex: senha errada no login) e nao deve abrir o modal global
    private static readonly AUTH_FLOW_PATHS = [
        '/auth/login',
        '/auth/register',
        '/auth/forgot-password',
        '/auth/reset-password',
        '/auth/social/callback',
        '/sanctum/csrf-cookie',
    ];

    private isAuthFlowEndpoint(url?: string): boolean {
        if (!url) return false;
        return ApiClient.AUTH_FLOW_PATHS.some(path => url.includes(path));
    }

    /**
     * Mapeia status HTTP + corpo da resposta para uma mensagem amigavel
     * em PT-BR / EN, substituindo o cru "Request failed with status code X"
     * que o axios coloca em error.message por padrao.
     *
     * Prioridade na escolha da mensagem:
     *   1. error.response.data.errors[primeiroCampo][0]  (validacao Laravel)
     *   2. error.response.data.message                   (mensagem do backend)
     *   3. fallback por status code                      (mensagens i18n)
     */
    private getFriendlyMessage(error: AxiosError): string {
        const status = error.response?.status;
        const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;

        // 422: tenta extrair primeira mensagem de validacao do Laravel
        if (status === 422 && data?.errors && typeof data.errors === 'object') {
            const firstField = Object.keys(data.errors)[0];
            const firstMessage = firstField && data.errors[firstField]?.[0];
            if (firstMessage) {
                return firstMessage;
            }
        }

        // Mensagem custom do backend (ja em PT-BR via Accept-Language)
        if (data?.message && typeof data.message === 'string' && !data.message.startsWith('Request failed')) {
            return data.message;
        }

        // Fallback por status code
        if (!error.response) {
            const code = (error as AxiosError & { code?: string }).code;
            if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') {
                return i18n.t('common:errors.timeout');
            }
            return i18n.t('common:errors.network');
        }

        switch (status) {
            case 400:
                return i18n.t('common:errors.bad_request');
            case 401:
                return i18n.t('common:errors.session_expired');
            case 403:
                return i18n.t('common:errors.forbidden');
            case 404:
                return i18n.t('common:errors.not_found');
            case 413:
                return i18n.t('common:errors.payload_too_large');
            case 422:
                return i18n.t('common:errors.validation_failed');
            case 429:
                return i18n.t('common:errors.rate_limited');
            case 500:
            case 502:
            case 504:
                return i18n.t('common:errors.server_error');
            default:
                return i18n.t('common:errors.unknown');
        }
    }

    // Handles response errors, including authentication issues and deploy/maintenance windows
    private async handleResponseError(error: AxiosError): Promise<unknown> {
        // 503 from API middleware or Caddy fallback during deploys — surface
        // a typed MaintenanceError so callers (pollTask, useBatchCreation) can
        // pause gracefully instead of treating this as a fatal failure.
        const maintenanceError = this.detectMaintenance(error);
        if (maintenanceError) {
            return Promise.reject(maintenanceError);
        }

        const status = error.response?.status;
        const requestUrl = error.config?.url;

        // 401 fora do fluxo de login: sessao expirou. Limpa token + estado de auth
        // e abre o AuthModal global. So abre na primeira deteccao (quando o store
        // ainda acha que o usuario esta autenticado) — evita reabrir em loop quando
        // pollings em background continuam batendo apos o usuario fechar o modal.
        if (status === 401 && !this.isAuthFlowEndpoint(requestUrl)) {
            const authState = useAuthStore.getState();
            const wasAuthenticated = authState.isAuthenticated;

            if (wasAuthenticated) {
                this.removeAuthToken();
                useAuthStore.setState({ user: null, isAuthenticated: false });

                const modal = useAuthModalStore.getState();
                if (!modal.isOpen) {
                    modal.openModal();
                }
            }
        }

        // Substitui "Request failed with status code X" por mensagem amigavel.
        // Preserva error.response, error.config, etc para callers que inspecionam.
        error.message = this.getFriendlyMessage(error);

        return Promise.reject(error);
    }

    // Set the authentication token (called after login)
    public setAuthToken(token: string): void {
        localStorage.setItem('auth_token', token);
        this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    // Remove the authentication token (called on logout)
    public removeAuthToken(): void {
        localStorage.removeItem('auth_token');
        delete this.client.defaults.headers.common['Authorization'];
    }

    // Get the current auth token
    public getAuthToken(): string | null {
        return localStorage.getItem('auth_token');
    }

    // Public methods to make HTTP requests
    public async get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.client.get<T>(url, config);
    }

    public async post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.client.post<T>(url, data, config);
    }

    public async put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.client.put<T>(url, data, config);
    }

    public async patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.client.patch<T>(url, data, config);
    }

    public async delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.client.delete<T>(url, config);
    }

    // Fetch CSRF cookie from backend (needed for routes protected by CSRF middleware)
    public async fetchCsrfToken(): Promise<void> {
        // Build root URL (strip trailing /api if present)
        const baseApiUrl = API_URL;
        const rootUrl = baseApiUrl.endsWith('/api')
            ? baseApiUrl.substring(0, baseApiUrl.lastIndexOf('/api'))
            : baseApiUrl;

        await axios.get(`${rootUrl}/sanctum/csrf-cookie`, {
            withCredentials: true,
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
        });
    }
}

// Export the ApiClient class itself
export { ApiClient };

// Get the singleton instance of ApiClient.
const apiClientSingletonInstance = ApiClient.getInstance();

// For debugging purposes - expose the singleton instance to the window object.
// @ts-expect-error We are intentionally exposing this for debugging in development.
window.debugApiClient = apiClientSingletonInstance;

// Export the singleton instance as a named export 'api'.
export const api = apiClientSingletonInstance;

// Export the singleton instance as the default export.
export default apiClientSingletonInstance;
